/**
 * @module config/features
 * @description Feature flags centralizados para controlar qué módulos están visibles en la UI.
 *
 * Los módulos con `false` no se eliminan del bundle — solo se ocultan del sidebar y
 * se bloquea el acceso por URL. El código sigue compilando y siendo importado.
 * Esto permite activar módulos en producción con un solo cambio de valor sin re-deploy.
 *
 * ## Estado actual de los flags
 * - `NETWORK_RA = false`: Dashboards Nacional/Red — desactivados (en desarrollo)
 * - Todos los demás: `true` — activos en producción
 *
 * ## Cómo agregar un nuevo módulo
 * 1. Agregar entrada en `FEATURES` con valor inicial `false` (desarrollo) o `true` (producción)
 * 2. Agregar mapeo en `VIEW_FEATURE_MAP` si el módulo tiene una vista con ID en el sidebar
 * 3. Agregar mapeo en `FRAMEWORK_FEATURE_MAP` si el módulo corresponde a un framework normativo
 * 4. Usar `isViewEnabled(viewId)` en el sidebar para mostrar/ocultar el ítem
 *
 * @see VIEW_FEATURE_MAP para el mapeo de IDs de vista a flags
 * @see FRAMEWORK_FEATURE_MAP para el mapeo de frameworks normativos a flags
 */

export const FEATURES = {
  /** ISO 27001/27002 - Panel principal de cumplimiento (siempre activo) */
  ISO27001: true,

  /** Ley Marco de Ciberseguridad (Ley 21.663) */
  LMCS: true,

  /** Gestión de Activos de Información (ISO 27005) */
  ASSETS: true,

  /** Registro de Eventos de Seguridad */
  SECURITY_EVENTS: true,

  /** Módulo de Auditoría */
  AUDIT: true,

  /** Reportes y Analítica */
  REPORTS: true,

  /** Progreso de implementación */
  PROGRESS: true,

  /** Dashboards de Red Asistencial (Nacional / Red) */
  NETWORK_RA: false,

  /** Certificaciones */
  CERTIFICATIONS: true,

  /** Asistente Conversacional Supervisado (ACS) */
  ACS: true,

  /** CIS Controls v8 — Center for Internet Security */
  CIS_CONTROLS: true,

  /** Modo Ambient / Monitoreo — overlay automático tras 15 min de inactividad */
  AMBIENT_MODE: true,
} as const;

/**
 * Mapeo de IDs de vista a feature flags.
 * Usado por el Sidebar y el sistema de rutas para verificar acceso.
 */
export const VIEW_FEATURE_MAP: Record<string, keyof typeof FEATURES> = {
  'dashboard': 'ISO27001',
  'audit': 'AUDIT',
  'assets': 'ASSETS',
  'security-events': 'SECURITY_EVENTS',
  'reports': 'REPORTS',
  'progress': 'PROGRESS',
  'incidents': 'LMCS',
  'national-ra': 'NETWORK_RA',
  'network-ra': 'NETWORK_RA',
  'certifications': 'CERTIFICATIONS',
  'acs': 'ACS',
  'cis': 'CIS_CONTROLS',
};

/**
 * Mapeo de códigos de framework a feature flags.
 * Usado por el FrameworkSelector del Header.
 */
export const FRAMEWORK_FEATURE_MAP: Record<string, keyof typeof FEATURES> = {
  'ISO27002': 'ISO27001',
  'ISO27001': 'ISO27001',
  'LMCS': 'LMCS',
  'CIS': 'CIS_CONTROLS',
};

/**
 * Verifica si una vista/módulo está habilitado.
 */
export function isViewEnabled(viewId: string): boolean {
  const featureKey = VIEW_FEATURE_MAP[viewId];
  if (!featureKey) return true; // Vistas sin mapeo están habilitadas por defecto
  return FEATURES[featureKey];
}

/**
 * Verifica si un framework está habilitado.
 */
export function isFrameworkEnabled(frameworkCode: string): boolean {
  const featureKey = FRAMEWORK_FEATURE_MAP[frameworkCode];
  if (!featureKey) return true;
  return FEATURES[featureKey];
}
